import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {Recipient} from '@gatewee/types/recipient';
import {
  ProviderTransactionQueryParams,
  ProviderTransactionsReportFilters,
} from '@gatewee/types/report';
import {PaginatedListResponse} from '@gatewee/types/system';
import {
  buildProviderTransactionColumns,
  countHiddenDataColumns,
  countVisibleDataColumns,
  estimateMaxVisibleColumns,
  estimateProviderTxContainerWidth,
  readProviderTransactionColumnPrefs,
  sortProviderTransactionKeys,
} from '@gatewee/utils';
import {TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';

export type ProviderTransactionSearchTerm =
  | 'e2eId'
  | 'transactionId'
  | 'externalId'
  | 'idempotencyKey';

export interface ProviderTransactionsModalState {
  recipient: Recipient;
  data: PaginatedListResponse<unknown>;
  page: number;
  pageSize: number;
  identifier: string;
  webhookResendEnabled: boolean;
}

export interface ProviderTransactionsModalDialogData {
  state$: BehaviorSubject<ProviderTransactionsModalState>;
  defaultReportFilters: ProviderTransactionsReportFilters;
  onFiltersApply: (filters: ProviderTransactionQueryParams) => void;
  onNewPage: (page: string) => void;
  onPageSizeChange: (size: number) => void;
  onResendWebhook: (ev: {
    row: Record<string, unknown>;
    selectedKey: string;
  }) => void;
  onExportReport: (filters: ProviderTransactionsReportFilters) => void;
}

@Component({
  selector: 'gatewee-web-provider-transactions-modal',
  templateUrl: './provider-transactions-modal.component.html',
  styleUrls: [
    './provider-transactions-modal.component.css',
    '../styles/gatewee-collapsible-details.css',
  ],
  standalone: false,
})
export class ProviderTransactionsModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  data!: PaginatedListResponse<unknown>;
  page = 1;
  identifier = 'default';
  recipient?: Recipient;
  webhookResendEnabled = false;

  columns: GateweeTableColumn[] = [];

  dataKeys: {label: string; value: string}[] = [];

  selectedKeyForWebhook: string | {label: string; value: string} = '';

  searchTerm: ProviderTransactionSearchTerm | {label: string; value: string} =
    'transactionId';

  searchValue = '';

  appliedSearchTerm: ProviderTransactionSearchTerm = 'transactionId';

  appliedSearchValue = '';

  reportFilters!: ProviderTransactionsReportFilters;

  maxVisibleColumns = estimateMaxVisibleColumns(
    estimateProviderTxContainerWidth(),
  );

  totalDataColumnCount = 0;

  pageSize = 10;

  readonly transactionTypeOptions: {label: string; value: string}[] = [
    {
      label:
        'features.recipients.providerTransactions.report.transactionTypeAll',
      value: '',
    },
    {
      label: 'features.recipients.providerTransactions.report.types.payment',
      value: 'payment',
    },
    {
      label: 'features.recipients.providerTransactions.report.types.refund',
      value: 'refund',
    },
    {
      label: 'features.recipients.providerTransactions.report.types.withdrawal',
      value: 'withdrawal',
    },
    {
      label: 'features.recipients.providerTransactions.report.types.chargeback',
      value: 'chargeback',
    },
  ];

  selectedTransactionType: string | {label: string; value: string} = '';

  readonly searchTermOptions: {
    value: ProviderTransactionSearchTerm;
    label: string;
  }[] = [
    {
      value: 'e2eId',
      label: 'features.recipients.providerTransactions.search.e2eId',
    },
    {
      value: 'transactionId',
      label: 'features.recipients.providerTransactions.search.transactionId',
    },
    {
      value: 'externalId',
      label: 'features.recipients.providerTransactions.search.externalId',
    },
    {
      value: 'idempotencyKey',
      label: 'features.recipients.providerTransactions.search.idempotencyKey',
    },
  ];

  private stateSub?: Subscription;
  private resizeTimer?: ReturnType<typeof setTimeout>;
  private modalResizeObserver?: ResizeObserver;

  @ViewChild('modalRoot', {read: ElementRef})
  private modalRoot?: ElementRef<HTMLElement>;

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      void,
      ProviderTransactionsModalDialogData
    >,
  ) {}

  ngOnInit(): void {
    this.reportFilters = {
      ...this.context.data.defaultReportFilters,
      ...this.buildDefaultDateRange(),
    };
    this.selectedTransactionType =
      this.transactionTypeOptions.find(
        option => option.value === (this.reportFilters.transactionType ?? ''),
      ) ?? this.transactionTypeOptions[0];
    this.searchTerm =
      this.searchTermOptions.find(option => option.value === 'transactionId') ??
      this.searchTermOptions[0];

    this.updateMaxVisibleColumns();

    this.stateSub = this.context.data.state$.subscribe(state => {
      this.data = state.data;
      this.page = state.page;
      this.pageSize = state.pageSize;
      this.recipient = state.recipient;
      this.identifier = state.identifier;
      this.webhookResendEnabled = state.webhookResendEnabled;
      if (state.recipient) {
        this.reportFilters = {
          ...this.reportFilters,
          recipientId: state.recipient.id,
          provider: state.recipient.provider,
        };
      }
      this.buildColumnsAndKeys();
    });
  }

  ngAfterViewInit(): void {
    const el = this.modalRoot?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.modalResizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width <= 0) {
        return;
      }
      this.applyContainerWidth(width);
    });
    this.modalResizeObserver.observe(el);
  }

  ngOnDestroy(): void {
    this.stateSub?.unsubscribe();
    this.modalResizeObserver?.disconnect();
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      const measured = this.modalRoot?.nativeElement?.clientWidth;
      this.applyContainerWidth(
        measured && measured > 0
          ? measured
          : estimateProviderTxContainerWidth(),
      );
    }, 150);
  }

  private applyContainerWidth(widthPx: number): void {
    const nextMax = estimateMaxVisibleColumns(widthPx);
    if (nextMax === this.maxVisibleColumns) {
      return;
    }
    this.maxVisibleColumns = nextMax;
    if (this.data?.records?.length) {
      this.buildColumnsAndKeys();
    }
  }

  dismiss(): void {
    this.context.completeWith();
  }

  private updateMaxVisibleColumns(): void {
    this.maxVisibleColumns = estimateMaxVisibleColumns(
      estimateProviderTxContainerWidth(),
    );
  }

  private get tableStorageKey(): string {
    return `${this.identifier}-table`;
  }

  private buildColumnsAndKeys(): void {
    if (!this.data?.records?.length) {
      // Keep columns on later pages so prev/next remains available when count is unknown.
      if (this.page <= 1) {
        this.columns = [];
        this.dataKeys = [];
        this.totalDataColumnCount = 0;
        this.selectedKeyForWebhook = '';
      }
      return;
    }

    const sample = this.data.records[0] as Record<string, unknown>;
    const keys = Object.keys(sample);
    const sortedKeys = sortProviderTransactionKeys(keys);

    this.dataKeys = sortedKeys.map(key => ({label: key, value: key}));
    this.totalDataColumnCount = keys.length;

    const savedPrefs = readProviderTransactionColumnPrefs(this.tableStorageKey);

    this.columns = buildProviderTransactionColumns(keys, {
      maxVisible: this.maxVisibleColumns,
      webhookResendEnabled: this.webhookResendEnabled,
      identifier: this.identifier,
      savedPrefs,
    });

    if (
      (this.selectedKeyForWebhook === '' ||
        (typeof this.selectedKeyForWebhook === 'object' &&
          !this.selectedKeyForWebhook?.value)) &&
      sortedKeys.length > 0
    ) {
      const defaultKey = sortedKeys.includes('transactionId')
        ? 'transactionId'
        : sortedKeys[0];
      this.selectedKeyForWebhook =
        this.dataKeys.find(k => k.value === defaultKey) ?? this.dataKeys[0];
    }
  }

  pageChange(e: string): void {
    this.context.data.onNewPage(e);
  }

  pageSizeChange(size: number): void {
    this.context.data.onPageSizeChange(size);
  }

  onRowAction(event: {row: unknown; type: string}): void {
    if (event.type === 'resendWebhook') {
      const raw = this.selectedKeyForWebhook;
      const selectedKey =
        typeof raw === 'string' ? raw : (raw as {value?: string})?.value ?? '';
      this.context.data.onResendWebhook({
        row: event.row as Record<string, unknown>,
        selectedKey,
      });
    }
  }

  applyFilters(): void {
    this.appliedSearchTerm = this.resolveSearchTerm();
    this.appliedSearchValue = this.searchValue.trim();
    this.context.data.onFiltersApply(this.buildQueryFilters());
  }

  onFiltersClear(): void {
    this.searchValue = '';
    this.appliedSearchValue = '';
    this.selectedTransactionType = this.transactionTypeOptions[0];
    this.reportFilters.onlyConfirmed = false;
    Object.assign(this.reportFilters, this.buildDefaultDateRange());
    this.context.data.onFiltersApply({});
  }

  onExportReportClick(): void {
    const rawType = this.selectedTransactionType;
    const transactionType =
      typeof rawType === 'string'
        ? rawType.trim()
        : (rawType as {value?: string})?.value?.trim() ?? '';

    const filters: ProviderTransactionsReportFilters = {
      ...this.reportFilters,
      transactionType: transactionType || undefined,
    };

    if (this.appliedSearchValue) {
      filters[this.appliedSearchTerm] = this.appliedSearchValue;
    }

    this.context.data.onExportReport(filters);
  }

  get isProviderTransactions(): boolean {
    return this.identifier === 'provider-transactions' && !!this.recipient;
  }

  get totalRecords(): number | null {
    const count = this.data?.count;
    return count !== undefined && count !== null && Number.isFinite(count)
      ? count
      : null;
  }

  get showTotalCount(): boolean {
    return this.totalRecords !== null;
  }

  get hasMorePages(): boolean {
    if (typeof this.data?.hasMore === 'boolean') {
      return this.data.hasMore;
    }
    if (this.totalRecords !== null) {
      return this.page * this.pageSize < this.totalRecords;
    }
    return (this.data?.records?.length ?? 0) >= this.pageSize;
  }

  get hasRecords(): boolean {
    return !!this.data?.records?.length;
  }

  get showTable(): boolean {
    return (
      (this.hasRecords && this.columns.length > 0) ||
      (this.page > 1 && !this.showTotalCount)
    );
  }

  get hasActiveSearch(): boolean {
    return this.searchValue.trim().length > 0;
  }

  get hasActiveFilters(): boolean {
    return (
      this.hasActiveSearch ||
      this.hasSelectedTransactionType ||
      this.reportFilters.onlyConfirmed ||
      this.hasCustomDateRange
    );
  }

  get hasCustomDateRange(): boolean {
    const defaults = this.buildDefaultDateRange();
    return (
      this.reportFilters.startDate !== defaults.startDate ||
      this.reportFilters.endDate !== defaults.endDate
    );
  }

  get hasAppliedSearch(): boolean {
    return this.appliedSearchValue.length > 0;
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.hasAppliedSearch) {
      count++;
    }
    if (this.hasSelectedTransactionType) {
      count++;
    }
    if (this.reportFilters.onlyConfirmed) {
      count++;
    }
    if (this.hasCustomDateRange) {
      count++;
    }
    return count;
  }

  get hasSelectedTransactionType(): boolean {
    const raw = this.selectedTransactionType;
    const value =
      typeof raw === 'string'
        ? raw.trim()
        : (raw as {value?: string})?.value?.trim() ?? '';
    return value.length > 0;
  }

  get showResultsToolbar(): boolean {
    return this.showHiddenColumnsHint || this.isProviderTransactions;
  }

  get appliedSearchTermLabelKey(): string {
    return `features.recipients.providerTransactions.search.${this.appliedSearchTerm}`;
  }

  get hiddenColumnCount(): number {
    return countHiddenDataColumns(this.columns);
  }

  get visibleDataColumnCount(): number {
    return countVisibleDataColumns(this.columns);
  }

  get tableSize(): 's' | 'm' {
    return this.totalDataColumnCount > this.maxVisibleColumns + 2 ? 's' : 'm';
  }

  get showHiddenColumnsHint(): boolean {
    return this.hiddenColumnCount > 0;
  }

  private resolveSearchTerm(): ProviderTransactionSearchTerm {
    const raw = this.searchTerm;
    if (typeof raw === 'string') {
      return raw;
    }
    return (raw.value as ProviderTransactionSearchTerm) ?? 'transactionId';
  }

  private resolveTransactionType(): string {
    const raw = this.selectedTransactionType;
    return typeof raw === 'string'
      ? raw.trim()
      : (raw as {value?: string})?.value?.trim() ?? '';
  }

  private buildQueryFilters(): ProviderTransactionQueryParams {
    const filters: ProviderTransactionQueryParams = {
      startDate: this.reportFilters.startDate,
      endDate: this.reportFilters.endDate,
      onlyConfirmed: this.reportFilters.onlyConfirmed,
    };

    const transactionType = this.resolveTransactionType();
    if (transactionType) {
      filters.transactionType = transactionType;
    }

    if (this.appliedSearchValue) {
      filters[this.appliedSearchTerm] = this.appliedSearchValue;
    }

    return filters;
  }

  private buildDefaultDateRange(): Pick<
    ProviderTransactionsReportFilters,
    'startDate' | 'endDate'
  > {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);

    return {
      startDate: this.formatDateOnly(start),
      endDate: this.formatDateOnly(end),
    };
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
